import { useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';

/** Gap between the anchor and the tooltip, and the least distance it keeps from the viewport edge. */
const TIP_GAP = 8;
const VIEWPORT_MARGIN = 8;

/** What a tooltip says, and the element it says it about. */
export interface MasterHint {
  text: string;
  /** The anchor's box in viewport coordinates, from `getBoundingClientRect()`. */
  anchor: DOMRect;
}

/**
 * A tooltip over the control it belongs to.
 *
 * <h2>Why not the native `title`, and why not PrimeReact's</h2>
 *
 * <p>A `title` attribute is the browser's: it waits about a second, draws in the operating system's style
 * rather than the application's, and inside a modal it is easy to miss entirely. PrimeReact's is bound once
 * by selector, which does not reach a disabled control (it receives no mouse events) nor anything rendered
 * after the binding.
 *
 * <p>So this: portalled to {@code <body>} and positioned {@code fixed}, which is what keeps a dialog's
 * scrolling body from clipping it. It opens above the anchor, drops below when there is no room above, and
 * is clamped inside the viewport on both axes with the arrow still over the anchor.
 *
 * <p>Extracted from the Manage Columns dialog, which needed exactly this and had it alone. A second copy is
 * how two tooltips in one application come to behave differently.
 */
export function MasterHintTooltip({ hint, id }: { hint: MasterHint; id: string }) {
  const ref = useRef<HTMLDivElement>(null);
  /* Hidden until measured, so it never flashes at the top-left before being placed. */
  const [style, setStyle] = useState<CSSProperties>({ visibility: 'hidden', top: 0, left: 0 });
  const [placement, setPlacement] = useState<'top' | 'bottom'>('top');

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const { width, height } = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const roomAbove = hint.anchor.top - TIP_GAP - VIEWPORT_MARGIN;
    const below = roomAbove < height;
    let top = below ? hint.anchor.bottom + TIP_GAP : hint.anchor.top - TIP_GAP - height;
    top = Math.max(VIEWPORT_MARGIN, Math.min(top, viewportHeight - height - VIEWPORT_MARGIN));

    const centre = hint.anchor.left + hint.anchor.width / 2;
    const left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(centre - width / 2, viewportWidth - width - VIEWPORT_MARGIN),
    );

    /* The arrow stays over the anchor even when the plate is pushed in from an edge. */
    const arrow = Math.max(12, Math.min(centre - left, width - 12));

    setPlacement(below ? 'bottom' : 'top');
    setStyle({ top, left, visibility: 'visible', ['--ms-tip-arrow' as string]: `${String(arrow)}px` });
  }, [hint]);

  return createPortal(
    <div ref={ref} id={id} role="tooltip" className={`ms-cols-tip ms-cols-tip--${placement}`} style={style}>
      {hint.text}
    </div>,
    document.body,
  );
}

/**
 * Opening and closing a hint from a control's own handlers.
 *
 * <p>Pointer and keyboard both, because a tooltip only a mouse can open is a tooltip half the users never
 * see. The anchor is measured on open rather than held as a ref: a dialog can scroll under it, and a stale
 * rectangle would place the plate where the control used to be.
 */
export function useMasterHint() {
  const [hint, setHint] = useState<MasterHint | null>(null);

  return {
    hint,
    show: (element: HTMLElement | null, text: string) => {
      if (element === null) return;
      setHint({ text, anchor: element.getBoundingClientRect() });
    },
    hide: () => {
      setHint(null);
    },
  };
}
